// existing.jsx (completely cleaned - no analysis display)
import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, BarChart, Building, Target, Rocket } from 'lucide-react';
import { FaCircleArrowLeft } from "react-icons/fa6";
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import axios from 'axios';
import { useAuthCheck } from '../hooks/useAuthCheck';

// Add this custom popup component before Existing function
const CustomPopup = ({ message, type, onClose, onConfirm, showConfirm }) => {
    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px'
        }} onClick={onClose}>
            <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: 'clamp(20px, 5vw, 32px)',
                maxWidth: 'min(400px, 90vw)',
                width: '100%',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                animation: 'slideIn 0.3s ease',
                textAlign: 'center'
            }} onClick={(e) => e.stopPropagation()}>
                <div style={{
                    fontSize: 'clamp(40px, 8vw, 60px)',
                    marginBottom: '16px'
                }}>
                    {type === 'success' ? '✅' : type === 'error' ? '❌' : type === 'warning' ? '⚠️' : 'ℹ️'}
                </div>
                <h4 style={{
                    color: type === 'error' ? '#dc3545' : type === 'warning' ? '#f59e0b' : '#0d53bd',
                    marginBottom: '16px',
                    fontSize: 'clamp(16px, 4vw, 20px)',
                    fontWeight: '600'
                }}>
                    {type === 'error' ? 'Oops!' : type === 'warning' ? 'Warning' : type === 'success' ? 'Success!' : 'Notice'}
                </h4>
                <p style={{
                    color: '#555',
                    marginBottom: '24px',
                    fontSize: 'clamp(14px, 3vw, 16px)',
                    lineHeight: '1.6'
                }}>
                    {message}
                </p>
                <div style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'center'
                }}>
                    {showConfirm ? (
                        <>
                            <button
                                onClick={onConfirm}
                                style={{
                                    background: 'linear-gradient(135deg, #0d53bd, #06b6d4)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '30px',
                                    padding: 'clamp(10px, 2vw, 12px) clamp(20px, 4vw, 32px)',
                                    fontSize: 'clamp(14px, 3vw, 16px)',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    flex: 1
                                }}
                                onMouseEnter={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 20px rgba(13, 83, 189, 0.3)'; }}
                                onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = 'none'; }}
                            >
                                Yes
                            </button>
                            <button
                                onClick={onClose}
                                style={{
                                    background: '#f3f4f6',
                                    color: '#666',
                                    border: 'none',
                                    borderRadius: '30px',
                                    padding: 'clamp(10px, 2vw, 12px) clamp(20px, 4vw, 32px)',
                                    fontSize: 'clamp(14px, 3vw, 16px)',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    flex: 1
                                }}
                                onMouseEnter={(e) => { e.target.style.background = '#e5e7eb'; }}
                                onMouseLeave={(e) => { e.target.style.background = '#f3f4f6'; }}
                            >
                                No
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={onClose}
                            style={{
                                background: 'linear-gradient(135deg, #0d53bd, #06b6d4)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '30px',
                                padding: 'clamp(10px, 2vw, 12px) clamp(24px, 5vw, 40px)',
                                fontSize: 'clamp(14px, 3vw, 16px)',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                width: '100%'
                            }}
                            onMouseEnter={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 20px rgba(13, 83, 189, 0.3)'; }}
                            onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = 'none'; }}
                        >
                            OK
                        </button>
                    )}
                </div>
            </div>
            <style>{`
                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};

function Existing() {
    useAuthCheck();

    const [step, setStep] = useState(0);
    const [form, setForm] = useState({
        businessName: '',
        industry: '',
        yearsInOperation: '',
        annualRevenue: '',
        teamSize: '',
        currentChallenges: '',
        growthGoals: '',
        targetMarket: '',
        marketingChannels: '',
        customerFeedback: '',
        // Additional fields for "other" options
        industryOther: '',
        yearsOther: '',
        revenueOther: '',
        teamSizeOther: '',
        marketingOther: '',
        feedbackOther: ''
    });

    const otherInputRefs = {
        industryOther: useRef(null),
        yearsOther: useRef(null),
        revenueOther: useRef(null),
        teamSizeOther: useRef(null),
        marketingOther: useRef(null),
        feedbackOther: useRef(null)
    };

    const [popup, setPopup] = useState({
        show: false,
        message: '',
        type: 'info',
        onConfirm: null,
        showConfirm: false
    });

    const showPopup = (message, type = 'info', showConfirm = false, onConfirm = null) => {
        setPopup({
            show: true,
            message,
            type,
            showConfirm,
            onConfirm
        });
    };

    const hidePopup = () => {
        setPopup(prev => ({ ...prev, show: false }));
    };

    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });
    }, []);

    useEffect(() => {
        if (form.industry === 'other' && otherInputRefs.industryOther.current) {
            otherInputRefs.industryOther.current.focus();
        }
    }, [form.industry]);

    useEffect(() => {
        if (form.yearsInOperation === 'other' && otherInputRefs.yearsOther.current) {
            otherInputRefs.yearsOther.current.focus();
        }
    }, [form.yearsInOperation]);

    useEffect(() => {
        if (form.annualRevenue === 'other' && otherInputRefs.revenueOther.current) {
            otherInputRefs.revenueOther.current.focus();
        }
    }, [form.annualRevenue]);

    useEffect(() => {
        if (form.teamSize === 'other' && otherInputRefs.teamSizeOther.current) {
            otherInputRefs.teamSizeOther.current.focus();
        }
    }, [form.teamSize]);

    useEffect(() => {
        if (form.marketingChannels === 'other' && otherInputRefs.marketingOther.current) {
            otherInputRefs.marketingOther.current.focus();
        }
    }, [form.marketingChannels]);

    useEffect(() => {
        if (form.customerFeedback === 'other' && otherInputRefs.feedbackOther.current) {
            otherInputRefs.feedbackOther.current.focus();
        }
    }, [form.customerFeedback]);

    const [loading, setLoading] = useState(false);

    const steps = [
        { title: "Business Basics", icon: <Building size={18} /> },
        { title: "Current Status", icon: <BarChart size={18} /> },
        { title: "Growth Goals", icon: <Target size={18} /> },
        { title: "Review & Analyze", icon: <Rocket size={18} /> }
    ];

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const nextStep = () => setStep((s) => Math.min(s + 1, steps.length - 1));
    const prevStep = () => setStep((s) => Math.max(s - 1, 0));

    const updateCreditsAfterAnalysis = (token) => {
        const user = localStorage.getItem('user');

        if (user) {
            try {
                const userData = JSON.parse(user);
                userData.credits = Math.max(0, (userData.credits || 0) - 1);
                localStorage.setItem('user', JSON.stringify(userData));
                window.dispatchEvent(new Event('storage'));
            } catch (e) {
                console.error('Error updating user credits:', e);
            }
        } else {
            const guestCredits = localStorage.getItem('guest_credits');
            const currentCredits = guestCredits ? parseInt(guestCredits) : 2;
            const newCredits = Math.max(0, currentCredits - 1);
            localStorage.setItem('guest_credits', newCredits.toString());

            if (newCredits === 0) {
                setTimeout(() => {
                    showPopup(
                        'You have used all your free credits! Would you like to login or sign up to get 5 more credits?',
                        'warning',
                        true,
                        () => {
                            window.location.href = '/login';
                        }
                    );
                }, 1000);
            }
        }
    };

    // existing.jsx - generateAnalysis function update
    const generateAnalysis = async () => {
        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            const user = localStorage.getItem('user');

            if (user) {
                try {
                    const userData = JSON.parse(user);
                    const credits = userData.credits || 0;

                    if (credits <= 0) {
                        showPopup('You have no credits remaining. Please purchase more credits to continue.', 'error');
                        setLoading(false);
                        return;
                    }
                } catch (e) {
                    console.error('Error parsing user data:', e);
                }
            } else {
                const guestCredits = localStorage.getItem('guest_credits');
                const creditsLeft = guestCredits ? parseInt(guestCredits) : 2;

                if (creditsLeft <= 0) {
                    showPopup(
                        'You have used all your free credits! Would you like to login or sign up to get 5 more credits?',
                        'warning',
                        true,
                        () => {
                            window.location.href = '/login';
                        }
                    );
                    setLoading(false);
                    return;
                }
            }

            const headers = {
                'Content-Type': 'application/json',
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // Prepare the data for backend
            const analysisData = {
                businessName: form.businessName || 'Your Business',
                industry: form.industry === 'other' ? form.industryOther :
                    (form.industry === 'tech' ? 'Technology & IT' :
                        form.industry === 'retail' ? 'Retail & E-commerce' :
                            form.industry === 'services' ? 'Professional Services' :
                                form.industry === 'manufacturing' ? 'Manufacturing' :
                                    form.industry === 'healthcare' ? 'Healthcare' :
                                        form.industry === 'finance' ? 'Finance & Banking' :
                                            form.industry === 'education' ? 'Education' :
                                                form.industry === 'hospitality' ? 'Hospitality & Tourism' :
                                                    form.industry === 'realestate' ? 'Real Estate' :
                                                        form.industry === 'agriculture' ? 'Agriculture' :
                                                            form.industry === 'transportation' ? 'Transportation' :
                                                                form.industry === 'energy' ? 'Energy' :
                                                                    form.industry === 'construction' ? 'Construction' :
                                                                        form.industry === 'entertainment' ? 'Entertainment' :
                                                                            form.industry === 'food' ? 'Food & Beverage' :
                                                                                form.industry === 'fashion' ? 'Fashion & Apparel' :
                                                                                    form.industry || 'Not specified'),
                yearsInOperation: form.yearsInOperation === 'other' ? form.yearsOther :
                    (form.yearsInOperation === '<1' ? 'Less than 1 year' :
                        form.yearsInOperation === '1-3' ? '1-3 years' :
                            form.yearsInOperation === '3-5' ? '3-5 years' :
                                form.yearsInOperation === '5-10' ? '5-10 years' :
                                    form.yearsInOperation === '10-15' ? '10-15 years' :
                                        form.yearsInOperation === '15-20' ? '15-20 years' :
                                            form.yearsInOperation === '20+' ? '20+ years' :
                                                form.yearsInOperation || 'Not specified'),
                annualRevenue: form.annualRevenue === 'other' ? form.revenueOther :
                    (form.annualRevenue === '<100k' ? 'Less than ₹100K' :
                        form.annualRevenue === '100k-500k' ? '₹100K - ₹500K' :
                            form.annualRevenue === '500k-1M' ? '₹500K - ₹1M' :
                                form.annualRevenue === '1M-5M' ? '₹1M - ₹5M' :
                                    form.annualRevenue === '5M-10M' ? '₹5M - ₹10M' :
                                        form.annualRevenue === '10M-50M' ? '₹10M - ₹50M' :
                                            form.annualRevenue === '50M+' ? '₹50M+' :
                                                form.annualRevenue || 'Not specified'),
                teamSize: form.teamSize === 'other' ? form.teamSizeOther :
                    (form.teamSize === '1-5' ? '1-5 employees' :
                        form.teamSize === '5-10' ? '5-10 employees' :
                            form.teamSize === '10-50' ? '10-50 employees' :
                                form.teamSize === '50-100' ? '50-100 employees' :
                                    form.teamSize === '100-250' ? '100-250 employees' :
                                        form.teamSize === '250-500' ? '250-500 employees' :
                                            form.teamSize === '500-1000' ? '500-1000 employees' :
                                                form.teamSize === '1000+' ? '1000+ employees' :
                                                    form.teamSize || 'Not specified'),
                targetMarket: form.targetMarket || 'Not specified',
                currentChallenges: form.currentChallenges || 'Not specified',
                growthGoals: form.growthGoals || 'Not specified',
                marketingChannels: form.marketingChannels === 'other' ? form.marketingOther :
                    (form.marketingChannels === 'social' ? 'Social Media' :
                        form.marketingChannels === 'seo' ? 'SEO & Content' :
                            form.marketingChannels === 'ppc' ? 'PPC Advertising' :
                                form.marketingChannels === 'email' ? 'Email Marketing' :
                                    form.marketingChannels === 'referral' ? 'Referral Programs' :
                                        form.marketingChannels === 'events' ? 'Events & Networking' :
                                            form.marketingChannels === 'print' ? 'Print Media' :
                                                form.marketingChannels === 'tv' ? 'TV & Radio' :
                                                    form.marketingChannels === 'influencer' ? 'Influencer Marketing' :
                                                        form.marketingChannels === 'affiliate' ? 'Affiliate Marketing' :
                                                            form.marketingChannels === 'multiple' ? 'Multiple Channels' :
                                                                form.marketingChannels || 'Not specified'),
                customerFeedback: form.customerFeedback === 'other' ? form.feedbackOther :
                    (form.customerFeedback === 'regular' ? 'Regularly collected' :
                        form.customerFeedback === 'monthly' ? 'Monthly surveys' :
                            form.customerFeedback === 'quarterly' ? 'Quarterly reviews' :
                                form.customerFeedback === 'annual' ? 'Annual feedback' :
                                    form.customerFeedback === 'occasional' ? 'Occasionally collected' :
                                        form.customerFeedback === 'informal' ? 'Informal feedback only' :
                                            form.customerFeedback === 'intermittent' ? 'Intermittent feedback' :
                                                form.customerFeedback === 'none' ? 'Not systematically collected' :
                                                    form.customerFeedback === 'planning' ? 'Planning to implement' :
                                                        form.customerFeedback || 'Not specified')
            };

            const response = await axios.post('https://insightdraft-backend.onrender.com/api/analyze-business', analysisData, {
                headers: headers,  // ✅ Use headers with token
                timeout: 120000
            });

            if (response.data.success && response.data.analysis) {
                updateCreditsAfterAnalysis(token);

                const backendAnalysis = response.data.analysis;

                // Direct mapping - backend se jo aaya wahi show karo
                const mappedAnalysis = {
                    currentPerformance: backendAnalysis.executiveSummary || 'Current performance analysis not available.',
                    swotAnalysis: backendAnalysis.swotAnalysis || 'SWOT analysis not available.',
                    growthOpportunities: backendAnalysis.growthStrategy || 'Growth opportunities analysis not available.',
                    riskAssessment: backendAnalysis.riskAssessment || 'Risk assessment not available.',
                    operationalEfficiency: backendAnalysis.implementationTimeline || 'Operational efficiency analysis not available.',
                    financialHealth: backendAnalysis.financialRecommendations || 'Financial health analysis not available.',
                    marketingStrategy: backendAnalysis.marketingPlan || 'Marketing strategy not available.',
                    competitiveAnalysis: backendAnalysis.marketAnalysis || 'Competitive analysis not available.',
                    customerAnalysis: backendAnalysis.customerAnalysis || 'Customer analysis not available.',
                    technologyAssessment: backendAnalysis.technologyAssessment || 'Technology assessment not available.',
                    teamOptimization: backendAnalysis.teamOptimization || 'Team optimization analysis not available.',
                    expansionStrategy: backendAnalysis.expansionStrategy || 'Expansion strategy not available.'
                };

                localStorage.setItem('businessAnalysis', JSON.stringify(mappedAnalysis));
                localStorage.setItem('businessFormData', JSON.stringify(form));
                localStorage.setItem('businessAnalysisTimestamp', Date.now().toString());
                localStorage.setItem('analysisType', 'existing');

                // Redirect to analysis overview page WITH TYPE
                window.location.href = '/analysis?type=existing';

            } else if (response.status === 403) {
                showPopup('No credits remaining. Please purchase more credits to continue.', 'error');
            } else {
                console.error("Backend returned error:", response.data.error);
                // Fallback to mock analysis
                const guestCredits = localStorage.getItem('guest_credits');
                const creditsLeft = guestCredits ? parseInt(guestCredits) : 2;
                if (creditsLeft > 0) {
                    createMockAnalysis();
                }
            }

        } catch (error) {
            const guestCredits = localStorage.getItem('guest_credits');
            const creditsLeft = guestCredits ? parseInt(guestCredits) : 2;
            if (creditsLeft > 0) {
                createMockAnalysis();
            }
        } finally {
            setLoading(false);
        }
    };

    // Fallback mock analysis function
    const createMockAnalysis = () => {
        const user = localStorage.getItem('user');
        const guestCredits = localStorage.getItem('guest_credits');

        let creditsLeft;
        if (user) {
            try {
                const userData = JSON.parse(user);
                creditsLeft = userData.credits || 0;
            } catch {
                creditsLeft = 0;
            }
        } else {
            creditsLeft = guestCredits ? parseInt(guestCredits) : 2;
        }

        if (creditsLeft <= 0) {
            showPopup('No credits remaining. Please login or purchase more credits.', 'error');
            return;
        }

        const mockAnalysis = {
            currentPerformance: `Current Performance Analysis for: ${form.businessName}\n\nOPERATIONAL METRICS:\n• Annual Revenue: ${form.annualRevenue || 'Not specified'}\n• Years in Operation: ${form.yearsInOperation || 'Not specified'}\n• Team Size: ${form.teamSize || 'Not specified'} employees\n• Industry: ${form.industry || 'Not specified'}\n\nKEY STRENGTHS:\n• Established brand presence in market\n• ${form.yearsInOperation || 'X'} years of industry experience\n• Revenue stability and growth trajectory\n• Customer base development`,
            swotAnalysis: `SWOT Analysis for: ${form.businessName}\n\nSTRENGTHS:\n• Established brand reputation\n• Industry experience of ${form.yearsInOperation || 'X'} years\n• Existing customer base and relationships\n• Revenue stream of ${form.annualRevenue || 'X'}\n\nWEAKNESSES:\n• Current challenges: ${form.currentChallenges || 'Not specified'}\n• Limited marketing channels\n• Potential operational inefficiencies\n• Market share constraints`,
            growthOpportunities: `Growth Opportunities for: ${form.businessName}\n\nIMMEDIATE OPPORTUNITIES:\n• Market expansion in ${form.targetMarket || 'current market'}\n• Product/service diversification\n• Digital transformation initiatives\n• Customer retention improvement\n\nLONG-TERM OPPORTUNITIES:\n• Geographic expansion\n• Strategic partnerships\n• Technology integration\n• Brand extension`,
            riskAssessment: `Risk Assessment for: ${form.businessName}\n\nHIGH PRIORITY RISKS:\n• Market competition intensification\n• Economic downturn impact\n• Technology disruption\n• Regulatory changes\n\nMITIGATION STRATEGIES:\n• Diversify revenue streams\n• Build cash reserves\n• Continuous innovation\n• Customer loyalty programs`,
            operationalEfficiency: `Operational Efficiency Analysis for: ${form.businessName}\n\nCURRENT EFFICIENCY METRICS:\n• Revenue per employee: To be calculated\n• Customer acquisition cost: Analysis needed\n• Operational overhead: Optimization required\n• Process automation potential: High\n\nIMPROVEMENT AREAS:\n• Streamline ${form.teamSize || 'team'} operations\n• Automate manual processes\n• Improve resource allocation\n• Enhance productivity tools`,
            financialHealth: `Financial Health Assessment for: ${form.businessName}\n\nCURRENT FINANCIAL STATUS:\n• Revenue: ${form.annualRevenue || 'Not specified'}\n• Profit margin analysis needed\n• Cash flow management required\n• Investment capacity assessment\n\nFINANCIAL RECOMMENDATIONS:\n• Revenue growth target: 25-40% annually\n• Cost optimization potential: 15-20%\n• Investment areas identified\n• Financial forecasting models`,
            marketingStrategy: `Marketing Strategy Analysis for: ${form.businessName}\n\nCURRENT CHANNELS:\n• ${form.marketingChannels || 'Limited channels identified'}\n• Digital presence assessment needed\n• Brand awareness metrics\n• Customer engagement analysis\n\nOPTIMIZATION STRATEGY:\n• Expand to 3-5 new channels\n• Increase marketing budget allocation\n• Implement data-driven campaigns\n• Enhance customer journey mapping`,
            competitiveAnalysis: `Competitive Analysis for: ${form.businessName}\n\nMARKET POSITION:\n• Current market share assessment\n• Competitive landscape mapping\n• Differentiation factors analysis\n• Pricing strategy evaluation\n\nCOMPETITIVE ADVANTAGE:\n• ${form.yearsInOperation || 'X'} years experience\n• Customer relationships\n• Industry knowledge\n• Brand recognition`,
            customerAnalysis: `Customer Analysis for: ${form.businessName}\n\nCUSTOMER INSIGHTS:\n• Target market: ${form.targetMarket || 'Not specified'}\n• Customer feedback: ${form.customerFeedback || 'Not collected'}\n• Retention rate analysis needed\n• Customer lifetime value calculation\n\nIMPROVEMENT RECOMMENDATIONS:\n• Enhanced customer service\n• Loyalty program development\n• Customer feedback system\n• Personalization initiatives`,
            technologyAssessment: `Technology Assessment for: ${form.businessName}\n\nCURRENT TECH STACK:\n• Technology adoption level assessment\n• Digital transformation readiness\n• Automation potential analysis\n• Integration opportunities\n\nTECHNOLOGY ROADMAP:\n• Implement CRM system\n• Adopt analytics tools\n• Automate repetitive tasks\n• Enhance online presence`,
            teamOptimization: `Team Optimization Plan for: ${form.businessName}\n\nCURRENT TEAM STRUCTURE:\n• Team size: ${form.teamSize || 'Not specified'} employees\n• Skill gap analysis required\n• Training needs assessment\n• Performance metrics evaluation\n\nOPTIMIZATION STRATEGY:\n• Cross-training programs\n• Leadership development\n• Remote work optimization\n• Productivity enhancement`,
            expansionStrategy: `Expansion Strategy for: ${form.businessName}\n\nGROWTH GOALS:\n• ${form.growthGoals || 'Growth targets not specified'}\n• Market expansion planning\n• Product/service diversification\n• Geographic reach extension\n\nEXECUTION PLAN:\n• Phase 1: Market research (1-2 months)\n• Phase 2: Pilot testing (2-3 months)\n• Phase 3: Full implementation (3-6 months)\n• Phase 4: Scaling (6-12 months)`
        };

        // Save mock analysis to localStorage
        localStorage.setItem('businessAnalysis', JSON.stringify(mockAnalysis));
        localStorage.setItem('businessFormData', JSON.stringify(form));
        localStorage.setItem('businessAnalysisTimestamp', Date.now().toString());
        localStorage.setItem('analysisType', 'existing');

        updateCreditsAfterAnalysis();
        window.location.href = '/analysis?type=existing';
    };

    // Step Forms (same as before, but shortened for brevity)
    const renderStepContent = () => {
        switch (step) {
            case 0:
                return (
                    <div className="row g-3 g-md-4">
                        {/* Business Basics Step */}
                        <div className="col-12 col-md-6">
                            <label className="form-label fw-semibold text-primary fs-6">Business Name 🏢</label>
                            <input
                                className="form-control border-0 fs-6"
                                name="businessName"
                                value={form.businessName}
                                onChange={handleChange}
                                placeholder="e.g. Tech Solutions Inc."
                                style={{
                                    background: '#f8f9fa',
                                    borderRadius: 12,
                                    padding: '12px 16px',
                                    border: '1px solid rgba(0,0,0,0.1)',
                                    transition: 'all 0.3s ease'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#0d53bd'}
                                onBlur={(e) => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                            />
                        </div>
                        <div className="col-12 col-md-6">
                            <label className="form-label fw-semibold text-primary fs-6">Industry</label>
                            <div className="position-relative">
                                {form.industry === 'other' ? (
                                    <div className="d-flex align-items-center">
                                        <input
                                            ref={otherInputRefs.industryOther}
                                            className="form-control border-0 fs-6"
                                            name="industryOther"
                                            value={form.industryOther || ''}
                                            onChange={(e) => setForm({ ...form, industryOther: e.target.value })}
                                            placeholder="Type your industry"
                                            style={{
                                                background: '#f8f9fa',
                                                borderRadius: 12,
                                                padding: '12px 16px',
                                                paddingRight: '50px',
                                                border: '1px solid rgba(0,0,0,0.1)',
                                                transition: 'all 0.3s ease'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#0d53bd'}
                                            onBlur={(e) => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-link position-absolute end-0"
                                            onClick={() => setForm({ ...form, industry: '', industryOther: '' })}
                                            style={{
                                                padding: '8px 16px',
                                                color: '#666',
                                                textDecoration: 'none',
                                                fontSize: '1.5rem',
                                                transition: 'all 0.3s ease',
                                                zIndex: 2
                                            }}
                                            title="Back to options"
                                            onMouseEnter={(e) => e.target.style.color = '#dc3545'}
                                            onMouseLeave={(e) => e.target.style.color = '#666'}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : (
                                    <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 0 }}>
                                        <InputLabel
                                            id="industry-select-label"
                                            sx={{
                                                color: '#6c757d',
                                                fontSize: '0.875rem',
                                                '&.Mui-focused': {
                                                    color: '#0d53bd'
                                                },
                                                transform: 'translate(14px, 11.5px) scale(1)',
                                                '&.MuiInputLabel-shrink': {
                                                    transform: 'translate(14px, -9px) scale(0.75)',
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '0 4px'
                                                }
                                            }}
                                        >
                                            Select industry
                                        </InputLabel>
                                        <Select
                                            labelId="industry-select-label"
                                            id="industry-select"
                                            name="industry"
                                            value={form.industry || ''}
                                            onChange={handleChange}
                                            label="Select industry"
                                            sx={{
                                                borderRadius: '12px',
                                                background: '#f8f9fa',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(0,0,0,0.1)',
                                                    borderRadius: '12px'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0d53bd'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0d53bd',
                                                    borderWidth: '2px'
                                                },
                                                '& .MuiSelect-select': {
                                                    padding: '11.5px 14px',
                                                    fontSize: '0.875rem',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        borderRadius: '12px',
                                                        marginTop: '8px',
                                                        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                                                        maxHeight: 300
                                                    }
                                                }
                                            }}
                                        >
                                            <MenuItem value="" sx={{ display: 'flex', alignItems: 'center' }}>
                                                <em>None</em>
                                            </MenuItem>
                                            <MenuItem value="tech" sx={{ display: 'flex', alignItems: 'center' }}>Technology & IT</MenuItem>
                                            <MenuItem value="retail" sx={{ display: 'flex', alignItems: 'center' }}>Retail & E-commerce</MenuItem>
                                            <MenuItem value="services" sx={{ display: 'flex', alignItems: 'center' }}>Professional Services</MenuItem>
                                            <MenuItem value="manufacturing" sx={{ display: 'flex', alignItems: 'center' }}>Manufacturing</MenuItem>
                                            <MenuItem value="healthcare" sx={{ display: 'flex', alignItems: 'center' }}>Healthcare</MenuItem>
                                            <MenuItem value="finance" sx={{ display: 'flex', alignItems: 'center' }}>Finance & Banking</MenuItem>
                                            <MenuItem value="education" sx={{ display: 'flex', alignItems: 'center' }}>Education</MenuItem>
                                            <MenuItem value="hospitality" sx={{ display: 'flex', alignItems: 'center' }}>Hospitality & Tourism</MenuItem>
                                            <MenuItem value="realestate" sx={{ display: 'flex', alignItems: 'center' }}>Real Estate</MenuItem>
                                            <MenuItem value="agriculture" sx={{ display: 'flex', alignItems: 'center' }}>Agriculture</MenuItem>
                                            <MenuItem value="transportation" sx={{ display: 'flex', alignItems: 'center' }}>Transportation</MenuItem>
                                            <MenuItem value="energy" sx={{ display: 'flex', alignItems: 'center' }}>Energy</MenuItem>
                                            <MenuItem value="construction" sx={{ display: 'flex', alignItems: 'center' }}>Construction</MenuItem>
                                            <MenuItem value="entertainment" sx={{ display: 'flex', alignItems: 'center' }}>Entertainment</MenuItem>
                                            <MenuItem value="food" sx={{ display: 'flex', alignItems: 'center' }}>Food & Beverage</MenuItem>
                                            <MenuItem value="fashion" sx={{ display: 'flex', alignItems: 'center' }}>Fashion & Apparel</MenuItem>
                                            <MenuItem value="other" sx={{ display: 'flex', alignItems: 'center' }}>Other (type industry)</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            </div>
                        </div>
                        <div className="col-12 col-md-6">
                            <label className="form-label fw-semibold text-primary fs-6">Years in Operation 📅</label>
                            <div className="position-relative">
                                {form.yearsInOperation === 'other' ? (
                                    <div className="d-flex align-items-center">
                                        <input
                                            ref={otherInputRefs.yearsOther}
                                            className="form-control border-0 fs-6"
                                            name="yearsOther"
                                            value={form.yearsOther || ''}
                                            onChange={(e) => setForm({ ...form, yearsOther: e.target.value })}
                                            placeholder="e.g. 15 years, 6 months"
                                            style={{
                                                background: '#f8f9fa',
                                                borderRadius: 12,
                                                padding: '12px 16px',
                                                paddingRight: '50px',
                                                border: '1px solid rgba(0,0,0,0.1)',
                                                transition: 'all 0.3s ease'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#0d53bd'}
                                            onBlur={(e) => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-link position-absolute end-0"
                                            onClick={() => setForm({ ...form, yearsInOperation: '', yearsOther: '' })}
                                            style={{
                                                padding: '8px 16px',
                                                color: '#666',
                                                textDecoration: 'none',
                                                fontSize: '1.5rem',
                                                transition: 'all 0.3s ease',
                                                zIndex: 2
                                            }}
                                            title="Back to options"
                                            onMouseEnter={(e) => e.target.style.color = '#dc3545'}
                                            onMouseLeave={(e) => e.target.style.color = '#666'}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : (
                                    <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 0 }}>
                                        <InputLabel
                                            id="years-select-label"
                                            sx={{
                                                color: '#6c757d',
                                                fontSize: '0.875rem',
                                                '&.Mui-focused': {
                                                    color: '#0d53bd'
                                                },
                                                transform: 'translate(14px, 11.5px) scale(1)',
                                                '&.MuiInputLabel-shrink': {
                                                    transform: 'translate(14px, -9px) scale(0.75)',
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '0 4px'
                                                }
                                            }}
                                        >
                                            Select years
                                        </InputLabel>
                                        <Select
                                            labelId="years-select-label"
                                            id="years-select"
                                            name="yearsInOperation"
                                            value={form.yearsInOperation || ''}
                                            onChange={handleChange}
                                            label="Select years"
                                            sx={{
                                                borderRadius: '12px',
                                                background: '#f8f9fa',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(0,0,0,0.1)',
                                                    borderRadius: '12px'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0d53bd'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0d53bd',
                                                    borderWidth: '2px'
                                                },
                                                '& .MuiSelect-select': {
                                                    padding: '11.5px 14px',
                                                    fontSize: '0.875rem',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        borderRadius: '12px',
                                                        marginTop: '8px',
                                                        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                                                        maxHeight: 250
                                                    }
                                                }
                                            }}
                                        >
                                            <MenuItem value="" sx={{ display: 'flex', alignItems: 'center' }}>
                                                <em>None</em>
                                            </MenuItem>
                                            <MenuItem value="<1" sx={{ display: 'flex', alignItems: 'center' }}>Less than 1 year</MenuItem>
                                            <MenuItem value="1-3" sx={{ display: 'flex', alignItems: 'center' }}>1-3 years</MenuItem>
                                            <MenuItem value="3-5" sx={{ display: 'flex', alignItems: 'center' }}>3-5 years</MenuItem>
                                            <MenuItem value="5-10" sx={{ display: 'flex', alignItems: 'center' }}>5-10 years</MenuItem>
                                            <MenuItem value="10-15" sx={{ display: 'flex', alignItems: 'center' }}>10-15 years</MenuItem>
                                            <MenuItem value="15-20" sx={{ display: 'flex', alignItems: 'center' }}>15-20 years</MenuItem>
                                            <MenuItem value="20+" sx={{ display: 'flex', alignItems: 'center' }}>20+ years</MenuItem>
                                            <MenuItem value="other" sx={{ display: 'flex', alignItems: 'center' }}>Other (type years)</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            </div>
                        </div>
                        <div className="col-12 col-md-6">
                            <label className="form-label fw-semibold text-primary fs-6">Annual Revenue 💰</label>
                            <div className="position-relative">
                                {form.annualRevenue === 'other' ? (
                                    <div className="d-flex align-items-center">
                                        <input
                                            ref={otherInputRefs.revenueOther}
                                            className="form-control border-0 fs-6"
                                            name="revenueOther"
                                            value={form.revenueOther || ''}
                                            onChange={(e) => setForm({ ...form, revenueOther: e.target.value })}
                                            placeholder="e.g. $10M, $50M+"
                                            style={{
                                                background: '#f8f9fa',
                                                borderRadius: 12,
                                                padding: '12px 16px',
                                                paddingRight: '50px',
                                                border: '1px solid rgba(0,0,0,0.1)',
                                                transition: 'all 0.3s ease'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#0d53bd'}
                                            onBlur={(e) => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-link position-absolute end-0"
                                            onClick={() => setForm({ ...form, annualRevenue: '', revenueOther: '' })}
                                            style={{
                                                padding: '8px 16px',
                                                color: '#666',
                                                textDecoration: 'none',
                                                fontSize: '1.5rem',
                                                transition: 'all 0.3s ease',
                                                zIndex: 2
                                            }}
                                            title="Back to options"
                                            onMouseEnter={(e) => e.target.style.color = '#dc3545'}
                                            onMouseLeave={(e) => e.target.style.color = '#666'}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : (
                                    <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 0 }}>
                                        <InputLabel
                                            id="revenue-select-label"
                                            sx={{
                                                color: '#6c757d',
                                                fontSize: '0.875rem',
                                                '&.Mui-focused': {
                                                    color: '#0d53bd'
                                                },
                                                transform: 'translate(14px, 11.5px) scale(1)',
                                                '&.MuiInputLabel-shrink': {
                                                    transform: 'translate(14px, -9px) scale(0.75)',
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '0 4px'
                                                }
                                            }}
                                        >
                                            Select revenue range
                                        </InputLabel>
                                        <Select
                                            labelId="revenue-select-label"
                                            id="revenue-select"
                                            name="annualRevenue"
                                            value={form.annualRevenue || ''}
                                            onChange={handleChange}
                                            label="Select revenue range"
                                            sx={{
                                                borderRadius: '12px',
                                                background: '#f8f9fa',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(0,0,0,0.1)',
                                                    borderRadius: '12px'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0d53bd'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0d53bd',
                                                    borderWidth: '2px'
                                                },
                                                '& .MuiSelect-select': {
                                                    padding: '11.5px 14px',
                                                    fontSize: '0.875rem',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        borderRadius: '12px',
                                                        marginTop: '8px',
                                                        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                                                        maxHeight: 250
                                                    }
                                                }
                                            }}
                                        >
                                            <MenuItem value="" sx={{ display: 'flex', alignItems: 'center' }}>
                                                <em>None</em>
                                            </MenuItem>
                                            <MenuItem value="<100k" sx={{ display: 'flex', alignItems: 'center' }}>Less than ₹100K</MenuItem>
                                            <MenuItem value="100k-500k" sx={{ display: 'flex', alignItems: 'center' }}>₹100K - ₹500K</MenuItem>
                                            <MenuItem value="500k-1M" sx={{ display: 'flex', alignItems: 'center' }}>₹500K - ₹1M</MenuItem>
                                            <MenuItem value="1M-5M" sx={{ display: 'flex', alignItems: 'center' }}>₹1M - ₹5M</MenuItem>
                                            <MenuItem value="5M-10M" sx={{ display: 'flex', alignItems: 'center' }}>₹5M - ₹10M</MenuItem>
                                            <MenuItem value="10M-50M" sx={{ display: 'flex', alignItems: 'center' }}>₹10M - ₹50M</MenuItem>
                                            <MenuItem value="50M+" sx={{ display: 'flex', alignItems: 'center' }}>₹50M+</MenuItem>
                                            <MenuItem value="other" sx={{ display: 'flex', alignItems: 'center' }}>Other (type revenue)</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            </div>
                        </div>
                    </div>
                );
            case 1:
                return (
                    <div className="row g-3 g-md-4">
                        {/* Current Status Step */}
                        <div className="col-12 col-md-6">
                            <label className="form-label fw-semibold text-primary fs-6">Team Size 👥</label>
                            <div className="position-relative">
                                {form.teamSize === 'other' ? (
                                    <div className="d-flex align-items-center">
                                        <input
                                            ref={otherInputRefs.teamSizeOther}
                                            className="form-control border-0 fs-6"
                                            name="teamSizeOther"
                                            value={form.teamSizeOther || ''}
                                            onChange={(e) => setForm({ ...form, teamSizeOther: e.target.value })}
                                            placeholder="e.g. 250 employees, 500+"
                                            style={{
                                                background: '#f8f9fa',
                                                borderRadius: 12,
                                                padding: '12px 16px',
                                                paddingRight: '50px',
                                                border: '1px solid rgba(0,0,0,0.1)',
                                                transition: 'all 0.3s ease'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#0d53bd'}
                                            onBlur={(e) => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-link position-absolute end-0"
                                            onClick={() => setForm({ ...form, teamSize: '', teamSizeOther: '' })}
                                            style={{
                                                padding: '8px 16px',
                                                color: '#666',
                                                textDecoration: 'none',
                                                fontSize: '1.5rem',
                                                transition: 'all 0.3s ease',
                                                zIndex: 2
                                            }}
                                            title="Back to options"
                                            onMouseEnter={(e) => e.target.style.color = '#dc3545'}
                                            onMouseLeave={(e) => e.target.style.color = '#666'}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : (
                                    <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 0 }}>
                                        <InputLabel
                                            id="team-size-select-label"
                                            sx={{
                                                color: '#6c757d',
                                                fontSize: '0.875rem',
                                                '&.Mui-focused': {
                                                    color: '#0d53bd'
                                                },
                                                transform: 'translate(14px, 11.5px) scale(1)',
                                                '&.MuiInputLabel-shrink': {
                                                    transform: 'translate(14px, -9px) scale(0.75)',
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '0 4px'
                                                }
                                            }}
                                        >
                                            Select team size
                                        </InputLabel>
                                        <Select
                                            labelId="team-size-select-label"
                                            id="team-size-select"
                                            name="teamSize"
                                            value={form.teamSize || ''}
                                            onChange={handleChange}
                                            label="Select team size"
                                            sx={{
                                                borderRadius: '12px',
                                                background: '#f8f9fa',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(0,0,0,0.1)',
                                                    borderRadius: '12px'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0d53bd'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0d53bd',
                                                    borderWidth: '2px'
                                                },
                                                '& .MuiSelect-select': {
                                                    padding: '11.5px 14px',
                                                    fontSize: '0.875rem',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        borderRadius: '12px',
                                                        marginTop: '8px',
                                                        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                                                        maxHeight: 250
                                                    }
                                                }
                                            }}
                                        >
                                            <MenuItem value="" sx={{ display: 'flex', alignItems: 'center' }}>
                                                <em>None</em>
                                            </MenuItem>
                                            <MenuItem value="1-5" sx={{ display: 'flex', alignItems: 'center' }}>1-5 employees</MenuItem>
                                            <MenuItem value="5-10" sx={{ display: 'flex', alignItems: 'center' }}>5-10 employees</MenuItem>
                                            <MenuItem value="10-50" sx={{ display: 'flex', alignItems: 'center' }}>10-50 employees</MenuItem>
                                            <MenuItem value="50-100" sx={{ display: 'flex', alignItems: 'center' }}>50-100 employees</MenuItem>
                                            <MenuItem value="100-250" sx={{ display: 'flex', alignItems: 'center' }}>100-250 employees</MenuItem>
                                            <MenuItem value="250-500" sx={{ display: 'flex', alignItems: 'center' }}>250-500 employees</MenuItem>
                                            <MenuItem value="500-1000" sx={{ display: 'flex', alignItems: 'center' }}>500-1000 employees</MenuItem>
                                            <MenuItem value="1000+" sx={{ display: 'flex', alignItems: 'center' }}>1000+ employees</MenuItem>
                                            <MenuItem value="other" sx={{ display: 'flex', alignItems: 'center' }}>Other (type size)</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            </div>
                        </div>
                        <div className="col-12 col-md-6">
                            <label className="form-label fw-semibold text-primary fs-6">Target Market 🎯</label>
                            <input
                                className="form-control border-0 fs-6"
                                name="targetMarket"
                                value={form.targetMarket}
                                onChange={handleChange}
                                placeholder="e.g. Small businesses in North America"
                                style={{
                                    background: '#f8f9fa',
                                    borderRadius: 12,
                                    padding: '12px 16px',
                                    border: '1px solid rgba(0,0,0,0.1)',
                                    transition: 'all 0.3s ease'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#0d53bd'}
                                onBlur={(e) => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                            />
                        </div>
                        <div className="col-12">
                            <label className="form-label fw-semibold text-primary fs-6">Current Challenges ⚠️</label>
                            <textarea
                                className="form-control border-0 fs-6"
                                name="currentChallenges"
                                value={form.currentChallenges}
                                onChange={handleChange}
                                rows={3}
                                placeholder="Describe your main business challenges..."
                                style={{
                                    background: '#f8f9fa',
                                    borderRadius: 12,
                                    padding: '12px 16px',
                                    border: '1px solid rgba(0,0,0,0.1)',
                                    transition: 'all 0.3s ease'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#0d53bd'}
                                onBlur={(e) => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                            />
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="row g-3 g-md-4">
                        {/* Growth Goals Step */}
                        <div className="col-12">
                            <label className="form-label fw-semibold text-primary fs-6">Growth Goals 🚀</label>
                            <textarea
                                className="form-control border-0 fs-6"
                                name="growthGoals"
                                value={form.growthGoals}
                                onChange={handleChange}
                                rows={3}
                                placeholder="Describe your business growth objectives..."
                                style={{
                                    background: '#f8f9fa',
                                    borderRadius: 12,
                                    padding: '12px 16px',
                                    border: '1px solid rgba(0,0,0,0.1)',
                                    transition: 'all 0.3s ease'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#0d53bd'}
                                onBlur={(e) => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                            />
                        </div>
                        <div className="col-12 col-md-6">
                            <label className="form-label fw-semibold text-primary fs-6">Marketing Channels 📢</label>
                            <div className="position-relative">
                                {form.marketingChannels === 'other' ? (
                                    <div className="d-flex align-items-center">
                                        <input
                                            ref={otherInputRefs.marketingOther}
                                            className="form-control border-0 fs-6"
                                            name="marketingOther"
                                            value={form.marketingOther || ''}
                                            onChange={(e) => setForm({ ...form, marketingOther: e.target.value })}
                                            placeholder="e.g. Trade shows, Direct mail"
                                            style={{
                                                background: '#f8f9fa',
                                                borderRadius: 12,
                                                padding: '12px 16px',
                                                paddingRight: '50px',
                                                border: '1px solid rgba(0,0,0,0.1)',
                                                transition: 'all 0.3s ease'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#0d53bd'}
                                            onBlur={(e) => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-link position-absolute end-0"
                                            onClick={() => setForm({ ...form, marketingChannels: '', marketingOther: '' })}
                                            style={{
                                                padding: '8px 16px',
                                                color: '#666',
                                                textDecoration: 'none',
                                                fontSize: '1.5rem',
                                                transition: 'all 0.3s ease',
                                                zIndex: 2
                                            }}
                                            title="Back to options"
                                            onMouseEnter={(e) => e.target.style.color = '#dc3545'}
                                            onMouseLeave={(e) => e.target.style.color = '#666'}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : (
                                    <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 0 }}>
                                        <InputLabel
                                            id="marketing-select-label"
                                            sx={{
                                                color: '#6c757d',
                                                fontSize: '0.875rem',
                                                '&.Mui-focused': {
                                                    color: '#0d53bd'
                                                },
                                                transform: 'translate(14px, 11.5px) scale(1)',
                                                '&.MuiInputLabel-shrink': {
                                                    transform: 'translate(14px, -9px) scale(0.75)',
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '0 4px'
                                                }
                                            }}
                                        >
                                            Select primary channels
                                        </InputLabel>
                                        <Select
                                            labelId="marketing-select-label"
                                            id="marketing-select"
                                            name="marketingChannels"
                                            value={form.marketingChannels || ''}
                                            onChange={handleChange}
                                            label="Select primary channels"
                                            sx={{
                                                borderRadius: '12px',
                                                background: '#f8f9fa',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(0,0,0,0.1)',
                                                    borderRadius: '12px'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0d53bd'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0d53bd',
                                                    borderWidth: '2px'
                                                },
                                                '& .MuiSelect-select': {
                                                    padding: '11.5px 14px',
                                                    fontSize: '0.875rem',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        borderRadius: '12px',
                                                        marginTop: '8px',
                                                        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                                                        maxHeight: 250
                                                    }
                                                }
                                            }}
                                        >
                                            <MenuItem value="" sx={{ display: 'flex', alignItems: 'center' }}>
                                                <em>None</em>
                                            </MenuItem>
                                            <MenuItem value="social" sx={{ display: 'flex', alignItems: 'center' }}>Social Media</MenuItem>
                                            <MenuItem value="seo" sx={{ display: 'flex', alignItems: 'center' }}>SEO & Content</MenuItem>
                                            <MenuItem value="ppc" sx={{ display: 'flex', alignItems: 'center' }}>PPC Advertising</MenuItem>
                                            <MenuItem value="email" sx={{ display: 'flex', alignItems: 'center' }}>Email Marketing</MenuItem>
                                            <MenuItem value="referral" sx={{ display: 'flex', alignItems: 'center' }}>Referral Programs</MenuItem>
                                            <MenuItem value="events" sx={{ display: 'flex', alignItems: 'center' }}>Events & Networking</MenuItem>
                                            <MenuItem value="print" sx={{ display: 'flex', alignItems: 'center' }}>Print Media</MenuItem>
                                            <MenuItem value="tv" sx={{ display: 'flex', alignItems: 'center' }}>TV & Radio</MenuItem>
                                            <MenuItem value="influencer" sx={{ display: 'flex', alignItems: 'center' }}>Influencer Marketing</MenuItem>
                                            <MenuItem value="affiliate" sx={{ display: 'flex', alignItems: 'center' }}>Affiliate Marketing</MenuItem>
                                            <MenuItem value="multiple" sx={{ display: 'flex', alignItems: 'center' }}>Multiple Channels</MenuItem>
                                            <MenuItem value="other" sx={{ display: 'flex', alignItems: 'center' }}>Other</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            </div>
                        </div>
                        <div className="col-12 col-md-6">
                            <label className="form-label fw-semibold text-primary fs-6">Customer Feedback 📝</label>
                            <div className="position-relative">
                                {form.customerFeedback === 'other' ? (
                                    <div className="d-flex align-items-center">
                                        <input
                                            ref={otherInputRefs.feedbackOther}
                                            className="form-control border-0 fs-6"
                                            name="feedbackOther"
                                            value={form.feedbackOther || ''}
                                            onChange={(e) => setForm({ ...form, feedbackOther: e.target.value })}
                                            placeholder="e.g. Monthly surveys, Customer advisory board"
                                            style={{
                                                background: '#f8f9fa',
                                                borderRadius: 12,
                                                padding: '12px 16px',
                                                paddingRight: '50px',
                                                border: '1px solid rgba(0,0,0,0.1)',
                                                transition: 'all 0.3s ease'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#0d53bd'}
                                            onBlur={(e) => e.target.style.borderColor = 'rgba(0,0,0,0.1)'}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-link position-absolute end-0"
                                            onClick={() => setForm({ ...form, customerFeedback: '', feedbackOther: '' })}
                                            style={{
                                                padding: '8px 16px',
                                                color: '#666',
                                                textDecoration: 'none',
                                                fontSize: '1.5rem',
                                                transition: 'all 0.3s ease',
                                                zIndex: 2
                                            }}
                                            title="Back to options"
                                            onMouseEnter={(e) => e.target.style.color = '#dc3545'}
                                            onMouseLeave={(e) => e.target.style.color = '#666'}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ) : (
                                    <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 0 }}>
                                        <InputLabel
                                            id="feedback-select-label"
                                            sx={{
                                                color: '#6c757d',
                                                fontSize: '0.875rem',
                                                '&.Mui-focused': {
                                                    color: '#0d53bd'
                                                },
                                                transform: 'translate(14px, 11.5px) scale(1)',
                                                '&.MuiInputLabel-shrink': {
                                                    transform: 'translate(14px, -9px) scale(0.75)',
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '0 4px'
                                                }
                                            }}
                                        >
                                            Select feedback status
                                        </InputLabel>
                                        <Select
                                            labelId="feedback-select-label"
                                            id="feedback-select"
                                            name="customerFeedback"
                                            value={form.customerFeedback || ''}
                                            onChange={handleChange}
                                            label="Select feedback status"
                                            sx={{
                                                borderRadius: '12px',
                                                background: '#f8f9fa',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(0,0,0,0.1)',
                                                    borderRadius: '12px'
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0d53bd'
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0d53bd',
                                                    borderWidth: '2px'
                                                },
                                                '& .MuiSelect-select': {
                                                    padding: '11.5px 14px',
                                                    fontSize: '0.875rem',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }
                                            }}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        borderRadius: '12px',
                                                        marginTop: '8px',
                                                        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                                                        maxHeight: 250
                                                    }
                                                }
                                            }}
                                        >
                                            <MenuItem value="" sx={{ display: 'flex', alignItems: 'center' }}>
                                                <em>None</em>
                                            </MenuItem>
                                            <MenuItem value="regular" sx={{ display: 'flex', alignItems: 'center' }}>Regularly collected</MenuItem>
                                            <MenuItem value="monthly" sx={{ display: 'flex', alignItems: 'center' }}>Monthly surveys</MenuItem>
                                            <MenuItem value="quarterly" sx={{ display: 'flex', alignItems: 'center' }}>Quarterly reviews</MenuItem>
                                            <MenuItem value="annual" sx={{ display: 'flex', alignItems: 'center' }}>Annual feedback</MenuItem>
                                            <MenuItem value="occasional" sx={{ display: 'flex', alignItems: 'center' }}>Occasionally collected</MenuItem>
                                            <MenuItem value="informal" sx={{ display: 'flex', alignItems: 'center' }}>Informal feedback only</MenuItem>
                                            <MenuItem value="intermittent" sx={{ display: 'flex', alignItems: 'center' }}>Intermittent feedback</MenuItem>
                                            <MenuItem value="none" sx={{ display: 'flex', alignItems: 'center' }}>Not systematically collected</MenuItem>
                                            <MenuItem value="planning" sx={{ display: 'flex', alignItems: 'center' }}>Planning to implement</MenuItem>
                                            <MenuItem value="other" sx={{ display: 'flex', alignItems: 'center' }}>Other</MenuItem>
                                        </Select>
                                    </FormControl>
                                )}
                            </div>
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="row g-3 g-md-4">
                        {/* Review Step */}
                        <div className="col-12">
                            <h5 className="fw-bold mb-3 text-primary fs-6">📋 Review Your Business Information</h5>
                            <div className="row g-2 g-md-3">
                                {[
                                    { label: '🏢 Business Name', value: form.businessName },
                                    {
                                        label: '🏭 Industry', value: form.industry === 'other' ? form.industryOther :
                                            (form.industry === 'tech' ? 'Technology & IT' :
                                                form.industry === 'retail' ? 'Retail & E-commerce' :
                                                    form.industry === 'services' ? 'Professional Services' :
                                                        form.industry === 'manufacturing' ? 'Manufacturing' :
                                                            form.industry === 'healthcare' ? 'Healthcare' :
                                                                form.industry === 'finance' ? 'Finance & Banking' :
                                                                    form.industry === 'education' ? 'Education' :
                                                                        form.industry === 'hospitality' ? 'Hospitality & Tourism' :
                                                                            form.industry === 'realestate' ? 'Real Estate' :
                                                                                form.industry === 'agriculture' ? 'Agriculture' :
                                                                                    form.industry === 'transportation' ? 'Transportation' :
                                                                                        form.industry === 'energy' ? 'Energy' :
                                                                                            form.industry === 'construction' ? 'Construction' :
                                                                                                form.industry === 'entertainment' ? 'Entertainment' :
                                                                                                    form.industry === 'food' ? 'Food & Beverage' :
                                                                                                        form.industry === 'fashion' ? 'Fashion & Apparel' :
                                                                                                            'Not specified')
                                    },
                                    {
                                        label: '📅 Years Operating', value: form.yearsInOperation === 'other' ? form.yearsOther :
                                            (form.yearsInOperation === '<1' ? 'Less than 1 year' :
                                                form.yearsInOperation === '1-3' ? '1-3 years' :
                                                    form.yearsInOperation === '3-5' ? '3-5 years' :
                                                        form.yearsInOperation === '5-10' ? '5-10 years' :
                                                            form.yearsInOperation === '10-15' ? '10-15 years' :
                                                                form.yearsInOperation === '15-20' ? '15-20 years' :
                                                                    form.yearsInOperation === '20+' ? '20+ years' :
                                                                        'Not specified')
                                    },
                                    {
                                        label: '💰 Annual Revenue', value: form.annualRevenue === 'other' ? form.revenueOther :
                                            (form.annualRevenue === '<100k' ? 'Less than ₹100K' :
                                                form.annualRevenue === '100k-500k' ? '₹100K - ₹500K' :
                                                    form.annualRevenue === '500k-1M' ? '₹500K - ₹1M' :
                                                        form.annualRevenue === '1M-5M' ? '₹1M - ₹5M' :
                                                            form.annualRevenue === '5M-10M' ? '₹5M - ₹10M' :
                                                                form.annualRevenue === '10M-50M' ? '₹10M - ₹50M' :
                                                                    form.annualRevenue === '50M+' ? '₹50M+' :
                                                                        'Not specified')
                                    },
                                    {
                                        label: '👥 Team Size', value: form.teamSize === 'other' ? form.teamSizeOther :
                                            (form.teamSize === '1-5' ? '1-5 employees' :
                                                form.teamSize === '5-10' ? '5-10 employees' :
                                                    form.teamSize === '10-50' ? '10-50 employees' :
                                                        form.teamSize === '50-100' ? '50-100 employees' :
                                                            form.teamSize === '100-250' ? '100-250 employees' :
                                                                form.teamSize === '250-500' ? '250-500 employees' :
                                                                    form.teamSize === '500-1000' ? '500-1000 employees' :
                                                                        form.teamSize === '1000+' ? '1000+ employees' :
                                                                            'Not specified')
                                    },
                                    { label: '🎯 Target Market', value: form.targetMarket },
                                    { label: '⚠️ Challenges', value: form.currentChallenges },
                                    { label: '🚀 Growth Goals', value: form.growthGoals },
                                    {
                                        label: '📢 Marketing Channels', value: form.marketingChannels === 'other' ? form.marketingOther :
                                            (form.marketingChannels === 'social' ? 'Social Media' :
                                                form.marketingChannels === 'seo' ? 'SEO & Content' :
                                                    form.marketingChannels === 'ppc' ? 'PPC Advertising' :
                                                        form.marketingChannels === 'email' ? 'Email Marketing' :
                                                            form.marketingChannels === 'referral' ? 'Referral Programs' :
                                                                form.marketingChannels === 'events' ? 'Events & Networking' :
                                                                    form.marketingChannels === 'print' ? 'Print Media' :
                                                                        form.marketingChannels === 'tv' ? 'TV & Radio' :
                                                                            form.marketingChannels === 'influencer' ? 'Influencer Marketing' :
                                                                                form.marketingChannels === 'affiliate' ? 'Affiliate Marketing' :
                                                                                    form.marketingChannels === 'multiple' ? 'Multiple Channels' :
                                                                                        'Not specified')
                                    },
                                    {
                                        label: '📝 Customer Feedback', value: form.customerFeedback === 'other' ? form.feedbackOther :
                                            (form.customerFeedback === 'regular' ? 'Regularly collected' :
                                                form.customerFeedback === 'monthly' ? 'Monthly surveys' :
                                                    form.customerFeedback === 'quarterly' ? 'Quarterly reviews' :
                                                        form.customerFeedback === 'annual' ? 'Annual feedback' :
                                                            form.customerFeedback === 'occasional' ? 'Occasionally collected' :
                                                                form.customerFeedback === 'informal' ? 'Informal feedback only' :
                                                                    form.customerFeedback === 'intermittent' ? 'Intermittent feedback' :
                                                                        form.customerFeedback === 'none' ? 'Not systematically collected' :
                                                                            form.customerFeedback === 'planning' ? 'Planning to implement' :
                                                                                'Not specified')
                                    }
                                ].map((item, idx) => (
                                    <div key={idx} className="col-12 col-md-6">
                                        <div className="p-2 p-md-3 rounded-3" style={{
                                            background: '#f8f9fa',
                                            border: '1px solid rgba(0,0,0,0.1)',
                                            transition: 'all 0.3s ease'
                                        }}>
                                            <strong>{item.label}:</strong> {item.value || 'Not specified'}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="alert alert-primary mt-3 mt-md-4 border-0" style={{
                                background: 'rgba(13, 83, 189, 0.1)',
                                borderRadius: 12,
                                border: '1px solid rgba(13, 83, 189, 0.2)',
                                transition: 'all 0.3s ease'
                            }}>
                                <strong>🚀 Ready for Analysis!</strong> Click "Generate Business Analysis" to get AI-powered optimization insights.
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="min-vh-100" style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0e7ff 100%)' }}>
            {popup.show && (
                <CustomPopup
                    message={popup.message}
                    type={popup.type}
                    onClose={hidePopup}
                    onConfirm={() => {
                        if (popup.onConfirm) popup.onConfirm();
                        hidePopup();
                    }}
                    showConfirm={popup.showConfirm}
                />
            )}
            <div className="container py-4 py-md-5 px-3 px-md-4" style={{ maxWidth: 1200 }}>
                {/* Header */}
                <div className="container-fluid px-0 mb-4 mb-md-5">
                    <div className="row align-items-center g-2">
                        <div className="col-auto">
                            <button
                                className="btn btn-outline-primary d-flex align-items-center justify-content-center order-1 order-md-0"
                                onClick={() => window.location.href = '/'}
                                style={{
                                    borderRadius: '12px',
                                    padding: 'clamp(8px, 2vw, 10px) clamp(12px, 2.5vw, 20px)',
                                    fontSize: 'clamp(14px, 3vw, 16px)',
                                    height: 'clamp(40px, 8vw, 44px)',
                                    minWidth: 'clamp(40px, 10vw, 120px)',
                                    flexShrink: 0,
                                    borderWidth: '2px',
                                    transition: 'all 0.3s ease',
                                    alignSelf: 'flex-start'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#0d53bd';
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#0d53bd';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                            >
                                <span className="d-none d-xl-inline-flex align-items-center">
                                    <FaCircleArrowLeft className="me-2" size={18} />
                                    Back to Home
                                </span>
                                <span className="d-none d-lg-inline-flex d-xl-none align-items-center">
                                    <FaCircleArrowLeft className="me-1" size={16} />
                                    Back
                                </span>
                                <span className="d-none d-md-inline-flex d-lg-none align-items-center">
                                    <FaCircleArrowLeft size={16} />
                                    <span className="ms-1">Back</span>
                                </span>
                                <span className="d-none d-sm-inline-flex d-md-none align-items-center justify-content-center"
                                    title="Back to Home">
                                    <FaCircleArrowLeft size={16} />
                                </span>
                                <span className="d-inline-flex d-sm-none align-items-center justify-content-center"
                                    title="Back to Home">
                                    <FaCircleArrowLeft size={16} />
                                </span>
                            </button>
                        </div>

                        <div className="col">
                            <div className="text-center">
                                <h1 className="fw-bold mb-1" style={{
                                    background: 'linear-gradient(90deg, #6366f1, #06b6d4)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                    fontSize: 'clamp(1.3rem, 3.8vw, 1.9rem)',
                                    lineHeight: '1.2',
                                    wordBreak: 'break-word',
                                    padding: '0 10px'
                                }}>
                                    Existing Business Analyzer
                                </h1>

                                <p className="text-muted m-0" style={{ fontSize: 'clamp(0.85rem, 1.6vw, 0.95rem)' }}>
                                    Optimize and scale your existing business with AI-powered insights
                                </p>
                            </div>
                        </div>

                        <div className="col-auto d-none d-lg-block" style={{ width: '140px' }}></div>
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4 mb-md-5 px-2" style={{
                    maxWidth: 'min(800px, 100%)',
                    margin: '0 auto'
                }}>
                    <div className="d-flex justify-content-between align-items-start mb-3 position-relative">
                        <div className="position-absolute top-0 start-0 end-0"
                            style={{
                                top: '20px',
                                height: '3px',
                                backgroundColor: '#e9ecef',
                                margin: '0 20px',
                                zIndex: 1
                            }}>
                        </div>

                        <div className="position-absolute top-0 start-0"
                            style={{
                                top: '20px',
                                height: '3px',
                                width: `${(step / (steps.length - 1)) * 100}%`,
                                background: 'linear-gradient(90deg, #6366f1, #06b6d4)',
                                margin: '0 20px',
                                zIndex: 2,
                                transition: 'width 0.4s ease'
                            }}>
                        </div>

                        {steps.map((stepItem, idx) => (
                            <div key={idx} className="text-center position-relative z-3" style={{ flex: 1 }}>
                                <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${idx <= step ? 'bg-primary' : 'bg-light'}`}
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        border: '3px solid white',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        position: 'relative'
                                    }}>
                                    {idx < step ? (
                                        <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
                                            <path d="M1 7L6 12L17 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    ) : idx === step ? (
                                        <div style={{
                                            animation: idx === step ? 'pulse 2s infinite' : 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            {React.cloneElement(stepItem.icon, {
                                                size: 20,
                                                color: idx <= step ? '#ffffff' : '#6c757d'
                                            })}
                                        </div>
                                    ) : (
                                        <div style={{
                                            opacity: 0.6,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            {React.cloneElement(stepItem.icon, {
                                                size: 20,
                                                color: '#6c757d'
                                            })}
                                        </div>
                                    )}
                                </div>

                                <div className="mt-2 px-1">
                                    <div className="d-none d-md-block" style={{
                                        color: idx <= step ? '#0d53bd' : '#6c757d',
                                        fontSize: '0.85rem',
                                        fontWeight: idx <= step ? 600 : 500,
                                        lineHeight: 1.2
                                    }}>
                                        {stepItem.title}
                                    </div>
                                    <div className="d-block d-md-none" style={{
                                        color: idx <= step ? '#0d53bd' : '#6c757d',
                                        fontSize: '0.7rem',
                                        fontWeight: idx <= step ? 600 : 500
                                    }}>
                                        {stepItem.title}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                            Progress
                        </span>
                        <div className="text-end">
                            <div style={{
                                fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                                fontWeight: 600,
                                color: '#0d53bd'
                            }}>
                                {Math.round(((step + 1) / steps.length) * 100)}%
                            </div>
                            <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                Step {step + 1} of {steps.length}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Step Forms */}
                <div className="row justify-content-center">
                    <div className="col-12 col-lg-10 col-xl-8">
                        <div className="card p-3 p-md-4 mb-4 border-0" style={{
                            borderRadius: 16,
                            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)'
                        }}>
                            {renderStepContent()}
                        </div>

                        {/* Navigation Buttons */}
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3 mt-4">
                            <button
                                className="btn btn-outline-primary d-flex align-items-center order-2 order-sm-1 w-100 w-sm-auto justify-content-center"
                                onClick={prevStep}
                                disabled={step === 0}
                                style={{
                                    borderRadius: 12,
                                    padding: '12px 24px',
                                    border: '1px solid #0d53bd',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#0d53bd';
                                    e.target.style.color = 'white';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.color = '#0d53bd';
                                }}
                            >
                                <ChevronLeft size={18} className="me-2" />
                                Previous
                            </button>

                            {step < steps.length - 1 ? (
                                <button
                                    className="btn btn-primary d-flex align-items-center order-1 order-sm-2 w-100 w-sm-auto justify-content-center"
                                    onClick={nextStep}
                                    disabled={
                                        (step === 0 && (!form.businessName || !form.industry || !form.yearsInOperation || !form.annualRevenue ||
                                            (form.industry === 'other' && !form.industryOther) ||
                                            (form.yearsInOperation === 'other' && !form.yearsOther) ||
                                            (form.annualRevenue === 'other' && !form.revenueOther))) ||
                                        (step === 1 && (!form.teamSize || !form.targetMarket || !form.currentChallenges ||
                                            (form.teamSize === 'other' && !form.teamSizeOther))) ||
                                        (step === 2 && (!form.growthGoals || !form.marketingChannels || !form.customerFeedback ||
                                            (form.marketingChannels === 'other' && !form.marketingOther) ||
                                            (form.customerFeedback === 'other' && !form.feedbackOther)))
                                    }
                                    style={{
                                        borderRadius: 12,
                                        padding: '12px 32px',
                                        background: '#0d53bd',
                                        border: 'none',
                                        transition: 'all 0.3s ease',
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = 'linear-gradient(90deg, #0d53bd, #1a6ae8)';
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 8px 20px rgba(13, 83, 189, 0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = '#0d53bd';
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                >
                                    Next
                                    <ChevronRight size={18} className="ms-2" />
                                </button>
                            ) : (
                                <button
                                    className="btn btn-success d-flex align-items-center order-1 order-sm-2 w-100 w-sm-auto justify-content-center"
                                    onClick={generateAnalysis}
                                    disabled={loading}
                                    style={{
                                        borderRadius: 12,
                                        padding: '12px 32px',
                                        background: 'linear-gradient(90deg, #10b981, #06b6d4)',
                                        border: 'none',
                                        transition: 'all 0.3s ease',
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!loading) {
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.3)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                >
                                    {loading ? (
                                        <>
                                            <div className="spinner-border spinner-border-sm me-2" />
                                            Analyzing...
                                        </>
                                    ) : (
                                        <>
                                            <Rocket size={18} className="me-2" />
                                            Generate Business Analysis
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Existing;